// src/routes/admin/workspace/index.tsx
import { createFileRoute, Link } from '@tanstack/react-router';
import {
  ArrowRight,
  ArrowUpRight,
  ClipboardList,
  HeartPulse,
  Landmark,
  type LucideIcon,
  MessagesSquare,
  Stethoscope,
  UserPlus,
  Users,
} from 'lucide-react';

import { useBusinessAssociate } from '@/features/business-associates/hooks';
import { useClients } from '@/features/clients/hooks/useClients';
import { usePractitioners } from '@/features/practitioners/hooks/usePractitioners';
import { useSessions } from '@/features/sessions/hooks/useSessions';
import { hasPermission, pickHighestAdminRole, type Permission, type RoleType } from '@/lib/rbac';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/stores/authStore';

export const Route = createFileRoute('/_staff/admin/workspace/')({
  component: WorkspaceDashboard,
  staticData: { crumb: 'Home' },
});

const ROLE_LABEL: Partial<Record<RoleType, string>> = {
  ba_super_admin: 'BA Super Admin',
  ba_admin: 'BA Admin',
};

interface WorkspaceEntry {
  to: string;
  icon: LucideIcon;
  title: string;
  description: string;
  /** Editorial small-caps tag — gives each department a magazine-section feel. */
  kicker?: string;
  /** Italic pull-quote rendered on the featured tile only. */
  pullQuote?: string;
  /** The role's north-star department; rendered as the 2-col mosaic tile. */
  featured?: boolean;
  permission?: Permission;
}

const WORKSPACES: WorkspaceEntry[] = [
  {
    to: '/admin/workspace/clients',
    icon: HeartPulse,
    title: 'Clients',
    kicker: 'Clinical · primary',
    pullQuote: 'Care begins at the chart.',
    description: 'Track every active client and the relationships in their care.',
    featured: true,
    permission: 'client:read',
  },
  {
    to: '/admin/workspace/organizations',
    icon: Landmark,
    title: 'Organizations',
    kicker: 'Structure',
    description: 'Practices, locations, and care teams inside your business associate.',
    permission: 'org:read',
  },
  {
    to: '/admin/workspace/practitioners',
    icon: Stethoscope,
    title: 'Practitioners',
    kicker: 'Care delivery',
    description: 'Practitioners delivering care for your BA.',
    permission: 'practitioner:read',
  },
  {
    to: '/admin/workspace/users',
    icon: Users,
    title: 'Users',
    kicker: 'People',
    description: 'Staff accounts in your workspace.',
    permission: 'user:read',
  },
];

const QUICK_ACTIONS: WorkspaceEntry[] = [
  {
    to: '/admin/workspace/users',
    icon: UserPlus,
    title: 'Invite user',
    description: 'Add someone to your workspace.',
    permission: 'user:invite',
  },
  {
    to: '/admin/workspace/organizations/new',
    icon: Landmark,
    title: 'Create organization',
    description: 'Set up a new organization with locations and team.',
    permission: 'org:create',
  },
  {
    to: '/admin/workspace/red-flags',
    icon: ClipboardList,
    title: 'Review red flags',
    description: 'Check flagged cases that need attention.',
    permission: 'red_flag:read',
  },
];

function timeOfDay(hour: number): 'morning' | 'afternoon' | 'evening' {
  if (hour < 12) return 'morning';
  if (hour < 18) return 'afternoon';
  return 'evening';
}

function WorkspaceDashboard() {
  const user = useAuthStore((s) => s.user);
  const role = user?.role ?? null;
  const firstName = user?.firstName ?? 'there';
  const adminRole = pickHighestAdminRole(role);
  const roleLabel = adminRole ? (ROLE_LABEL[adminRole] ?? adminRole) : '';
  const { data: businessAssociate } = useBusinessAssociate(user?.businessAssociateId ?? undefined);
  const baName = businessAssociate?.name ?? 'your organization';

  const visibleWorkspaces = WORKSPACES.filter(
    (w) => !w.permission || hasPermission(role, w.permission),
  );
  const visibleActions = QUICK_ACTIONS.filter(
    (a) => !a.permission || hasPermission(role, a.permission),
  );

  // Promote the role's north-star department (Clients). If RBAC hides it,
  // fall back to the first visible workspace so the mosaic always has a
  // featured slot.
  const featured = visibleWorkspaces.find((w) => w.featured) ?? visibleWorkspaces[0];
  const standards = featured ? visibleWorkspaces.filter((w) => w !== featured) : visibleWorkspaces;

  const now = new Date();
  const greetingPrompt = `Good ${timeOfDay(now.getHours())},`;
  const today = now.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });

  // RBAC-gated visibility flags for the ledger stats. Each stat is rendered
  // by its own sub-component so the underlying React Query hook only fires
  // when the user can actually read that resource — no wasted 403s.
  const canSeeClients = hasPermission(role, 'client:read');
  const canSeeSessions = hasPermission(role, 'session:read');
  const canSeePractitioners = hasPermission(role, 'practitioner:read');
  const hasAnyStat = canSeeClients || canSeeSessions || canSeePractitioners;

  return (
    <div className={cn('mx-auto max-w-6xl space-y-8 pb-10')}>
      {/* I — Hero ─────────────────────────────────────────────────────
         A composed editorial cover. The text column carries the
         typographic signature (italic prompt + roman name); a right
         rail carries three RBAC-gated stats pulled from real backend
         queries — clients in care, sessions today, practitioners. Each
         stat is its own sub-component so the React Query hook only
         fires when the user can read that resource (no wasted 403s, no
         "—" placeholders for missing permissions).

         Two atmospheric layers (radial mint glow top-right, soft
         primary-50 wash bottom-left) give the card visual depth and
         brand warmth without competing with the typography. Card height
         collapses to its natural content height; on viewports below sm
         the right rail tucks beneath the text column with a hairline
         top divider. */}
      <header className="bg-surface border-border relative isolate overflow-hidden rounded-2xl border shadow-sm">
        {/* Atmospheric layer 1 — soft mint glow drifting in from the
           top-right corner. Sits behind everything; gives the hero a
           sense of light without committing to a hard gradient. */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -top-32 -right-24 h-80 w-80 rounded-full bg-[radial-gradient(circle,var(--color-primary-100)_0%,transparent_60%)] opacity-60 blur-3xl"
        />
        {/* Atmospheric layer 2 — a faint primary-50 wash from the
           bottom-left, anchoring the page voice on the text column. */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -bottom-24 -left-20 h-72 w-72 rounded-full bg-[radial-gradient(circle,var(--color-primary-50)_0%,transparent_65%)] opacity-50 blur-3xl"
        />

        <div
          className={cn(
            'relative grid gap-0',
            hasAnyStat
              ? 'sm:grid-cols-[minmax(0,1fr)_300px] lg:grid-cols-[minmax(0,1fr)_340px]'
              : '',
          )}
        >
          {/* Text column — justify-between distributes content top vs.
             bottom so the dot-rule + prompt anchor to the foot of the
             column instead of leaving a tall empty stretch when the
             stats rail extends the card height. */}
          <div className="flex flex-col justify-between gap-6 px-7 py-7 sm:px-9 sm:py-8">
            <div>
              <div className="text-meta-sm tracking-eyebrow flex items-center gap-2 font-semibold text-neutral-400 uppercase">
                <span>{today}</span>
                {roleLabel ? (
                  <>
                    <span aria-hidden="true" className="text-neutral-300">
                      —
                    </span>
                    <span className="text-accent-ink/70">{roleLabel}</span>
                  </>
                ) : null}
              </div>

              {/* Italic-prompt + roman-name pairing at display-lg (32px).
                 The prompt is set at 80% ink, the name at full ink with
                 font-medium, so the eye lands on the name as the anchor. */}
              <h1 className="mt-3">
                <span className="font-display text-display-lg text-accent-ink/80 block leading-none tracking-[-0.015em] italic">
                  {greetingPrompt}
                </span>
                <span className="text-display-lg text-accent-ink mt-1 block leading-[1.05] font-medium tracking-[-0.02em]">
                  {firstName}.
                </span>
              </h1>

              <p className="text-body-md mt-3 max-w-md leading-relaxed text-neutral-500">
                Welcome back. Pick up where you left off across {baName}.
              </p>
            </div>

            {/* Mint-dot rule + italic prompt — a single inline row that
               sits at the foot of the text column. Pattern lifted from
               the .prose-editorial hr styleset. */}
            <div className="flex items-center gap-3">
              <div
                aria-hidden="true"
                className="h-3 w-12 shrink-0 bg-[radial-gradient(circle,var(--color-primary-300)_1.5px,transparent_1.5px)] bg-size-[14px_12px] bg-position-[left_center] bg-repeat-x"
              />
              <p className="font-display text-body-sm text-accent-ink/70 italic">
                Where shall we begin today?
              </p>
            </div>
          </div>

          {/* Right rail — stats panel. Same surface as the rest of the
             card (no tinted bg) so it doesn't blend into the mint-paper
             page background; visual separation comes from the hairline
             left divider on sm+ (top divider on mobile) and the icon
             chips inside each stat row. */}
          {hasAnyStat ? (
            <div className="border-border flex flex-col border-t sm:border-t-0 sm:border-l">
              {canSeeClients ? <ClientsStat /> : null}
              {canSeeSessions ? <SessionsTodayStat /> : null}
              {canSeePractitioners ? <PractitionersStat /> : null}
            </div>
          ) : null}
        </div>
      </header>

      {/* II — Workspaces ──────────────────────────────────────────────
         Single composed card holding all departments — header strip with
         the roman numeral + count, then the tile grid as the card body.
         The featured department spans the full row (lead story) and the
         remaining departments sit in a 3-col grid below (secondary
         stories), so the layout reads as a magazine contents page rather
         than a portal of identical squares. */}
      {visibleWorkspaces.length > 0 ? (
        <section className="bg-surface border-border overflow-hidden rounded-2xl border shadow-xs">
          <header className="border-border flex items-baseline gap-3 border-b px-6 py-5">
            <span className="font-display text-title-md text-primary-500 italic tabular-nums">
              I.
            </span>
            <h2 className="text-label-md tracking-label text-accent-ink font-semibold uppercase">
              Workspaces
            </h2>
            <span className="font-display text-meta-sm ml-auto text-neutral-400 italic">
              {`${String(visibleWorkspaces.length)} ${visibleWorkspaces.length === 1 ? 'department' : 'departments'}`}
            </span>
          </header>

          <div className="grid grid-cols-1 gap-3 p-5 sm:grid-cols-3 sm:gap-4 sm:p-6">
            {featured ? <FeaturedTile entry={featured} /> : null}
            {standards.map((w) => (
              <StandardTile key={w.to} entry={w} />
            ))}
          </div>
        </section>
      ) : null}

      {/* III — Quick actions ─────────────────────────────────────────
         Two-tone composed card. The outer shell carries the same frame
         as the Workspaces card (rounded-2xl, hairline border, soft
         elevation), but the body fills edge-to-edge with deep accent-
         ink — the outer `overflow-hidden` clips the fill to the card's
         rounded corners, so we get a clean white header strip on top
         and a dark action zone underneath, divided by a single hairline
         rule. No nested cards, no white frame around the dark band. */}
      {visibleActions.length > 0 ? (
        <section className="bg-surface border-border overflow-hidden rounded-2xl border shadow-xs">
          <header className="border-border flex items-baseline gap-3 border-b px-6 py-5">
            <span className="font-display text-title-md text-primary-500 italic tabular-nums">
              II.
            </span>
            <h2 className="text-label-md tracking-label text-accent-ink font-semibold uppercase">
              Quick actions
            </h2>
            <span className="font-display text-meta-sm ml-auto text-neutral-400 italic">
              {`${String(visibleActions.length)} ${visibleActions.length === 1 ? 'shortcut' : 'shortcuts'}`}
            </span>
          </header>

          {/* Dark body — flush to the card edges. The atmospheric mint
             glows live directly on this surface; row chips (mint-paper
             icon + white-on-ink text) read clearly against the deep
             ink ground without any nested rounded panel. */}
          <div className="bg-accent-ink relative overflow-hidden px-5 py-6 sm:px-6 sm:py-7">
            <div
              aria-hidden="true"
              className="pointer-events-none absolute -top-32 -right-24 h-80 w-80 rounded-full bg-[radial-gradient(circle,var(--color-primary-300)_0%,transparent_60%)] opacity-40 blur-2xl"
            />
            <div
              aria-hidden="true"
              className="pointer-events-none absolute -bottom-40 -left-32 h-72 w-72 rounded-full bg-[radial-gradient(circle,var(--color-primary-500)_0%,transparent_60%)] opacity-25 blur-3xl"
            />
            <div className="relative grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
              {visibleActions.map((a) => (
                <DarkActionRow key={a.title} entry={a} />
              ))}
            </div>
          </div>
        </section>
      ) : null}

      {/* Footer signature — final editorial flourish. */}
      <p className="font-display text-meta-sm tracking-eyebrow text-center text-neutral-400 uppercase italic">
        Myndwell &middot; clinical operations
      </p>
    </div>
  );
}

// ── Hero ledger stats ────────────────────────────────────────────────
// One sub-component per stat so the underlying React Query hook only
// fires when the parent has decided the user can read that resource.
// Each component renders a `StatBlock` with an italic SemiMono numeral,
// a small-caps label, and a one-word italic sublabel for context.

function ClientsStat() {
  const { data, isLoading } = useClients({ limit: 1 });
  return (
    <StatBlock
      icon={HeartPulse}
      label="Clients"
      value={data?.pagination.total}
      sublabel="in care"
      isLoading={isLoading}
    />
  );
}

function SessionsTodayStat() {
  const today = new Date().toISOString().slice(0, 10);
  const { data, isLoading } = useSessions({ limit: 1, from: today, to: today });
  return (
    <StatBlock
      icon={MessagesSquare}
      label="Sessions"
      value={data?.pagination.total}
      sublabel="today"
      isLoading={isLoading}
    />
  );
}

function PractitionersStat() {
  const { data, isLoading } = usePractitioners({ limit: 1 });
  return (
    <StatBlock
      icon={Stethoscope}
      label="Practitioners"
      value={data?.pagination.total}
      sublabel="delivering care"
      isLoading={isLoading}
    />
  );
}

function StatBlock({
  icon: Icon,
  label,
  value,
  sublabel,
  isLoading,
}: {
  icon: LucideIcon;
  label: string;
  value: number | undefined;
  sublabel: string;
  isLoading: boolean;
}) {
  // `—` covers both the in-flight load and the rare error/undefined case;
  // `0` renders as a real zero (a meaningful number, not a placeholder).
  const display = isLoading || value == null ? '—' : String(value);
  return (
    <div className="border-border flex flex-1 items-center gap-4 px-6 py-5 not-last:border-b sm:px-7">
      <div className="bg-primary-50 text-accent-ink flex h-10 w-10 shrink-0 items-center justify-center rounded-lg">
        <Icon className="h-5 w-5" strokeWidth={1.5} aria-hidden="true" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-meta-sm tracking-eyebrow font-semibold text-neutral-500 uppercase">
          {label}
        </div>
        <div className="mt-1 flex items-baseline gap-2">
          <span
            aria-live="polite"
            className="font-display text-accent-ink text-[36px] leading-none tracking-[-0.02em] italic tabular-nums"
          >
            {display}
          </span>
          <span className="font-display text-body-sm text-neutral-500 italic">{sublabel}</span>
        </div>
      </div>
    </div>
  );
}

function FeaturedTile({ entry }: { entry: WorkspaceEntry }) {
  const Icon = entry.icon;
  return (
    <Link
      to={entry.to as never}
      className={cn(
        'group bg-mint-paper border-border relative flex items-start gap-5 overflow-hidden rounded-xl border p-6 shadow-xs',
        'sm:col-span-3',
        'transition-all duration-200 ease-out',
        'hover:border-primary-300 hover:-translate-y-0.5 hover:shadow-md',
        'focus-visible:ring-ring focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none',
        'motion-reduce:transform-none motion-reduce:hover:translate-y-0',
      )}
    >
      {/* Atmospheric corner glow — the only ornament the featured tile gets,
         and it earns its keep by signalling "this is the principal door"
         without resorting to a louder background or a fake stat number. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -right-20 -bottom-20 h-56 w-56 rounded-full bg-[radial-gradient(circle,var(--color-primary-200)_0%,transparent_65%)] opacity-50 blur-2xl"
      />

      {/* Icon chip — left rail, vertically aligned with the title cluster.
         Square 12 with a soft white-on-mint contrast against the tile bg. */}
      <div className="bg-surface text-primary-500 group-hover:bg-primary-500 group-hover:text-primary-foreground relative flex h-12 w-12 shrink-0 items-center justify-center rounded-xl shadow-xs transition-colors">
        <Icon className="h-5 w-5" strokeWidth={1.5} aria-hidden="true" />
      </div>

      {/* Content cluster — fills the middle, max-w on the description so
         long lines don't sprawl on wide cards. */}
      <div className="relative min-w-0 flex-1 space-y-1.5 pt-0.5">
        {entry.kicker ? (
          <span className="text-meta-sm tracking-eyebrow text-primary-600 block font-semibold uppercase">
            {entry.kicker}
          </span>
        ) : null}
        <h3 className="font-display text-display-sm text-accent-ink leading-tight font-medium">
          {entry.title}
        </h3>
        {entry.pullQuote ? (
          <p className="font-display text-body-md text-primary-700/85 italic">
            &ldquo;{entry.pullQuote}&rdquo;
          </p>
        ) : null}
        <p className="text-body-sm max-w-xl leading-snug text-neutral-600">{entry.description}</p>
      </div>

      {/* Arrow — anchored top-right of the tile so it reads as the
         "enter" affordance for the whole row. */}
      <ArrowUpRight
        className={cn(
          'relative h-5 w-5 shrink-0 self-start text-neutral-400 transition-all duration-200 ease-out',
          'group-hover:text-primary-600 group-hover:translate-x-0.5 group-hover:-translate-y-0.5',
        )}
        strokeWidth={1.5}
        aria-hidden="true"
      />
    </Link>
  );
}

function StandardTile({ entry }: { entry: WorkspaceEntry }) {
  const Icon = entry.icon;
  return (
    <Link
      to={entry.to as never}
      className={cn(
        'group bg-surface relative flex h-full flex-col gap-3 rounded-xl border border-(--color-border) p-5 shadow-xs',
        'transition-all duration-200 ease-out',
        'hover:border-primary-200 hover:-translate-y-0.5 hover:shadow-md',
        'focus-visible:ring-ring focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none',
        'motion-reduce:transform-none motion-reduce:hover:translate-y-0',
      )}
    >
      <div className="flex items-start justify-between">
        <div className="bg-primary-50 text-accent-ink group-hover:bg-primary-500 group-hover:text-primary-foreground flex h-10 w-10 items-center justify-center rounded-lg transition-colors">
          <Icon className="h-5 w-5" strokeWidth={1.5} aria-hidden="true" />
        </div>
        <ArrowUpRight
          className={cn(
            'h-4 w-4 text-neutral-300 transition-all duration-200 ease-out',
            'group-hover:text-primary-500 group-hover:translate-x-0.5 group-hover:-translate-y-0.5',
          )}
          strokeWidth={1.75}
          aria-hidden="true"
        />
      </div>

      <div className="mt-auto min-w-0 space-y-1.5">
        {entry.kicker ? (
          <span className="text-meta-sm tracking-eyebrow font-semibold text-neutral-400 uppercase">
            {entry.kicker}
          </span>
        ) : null}
        <h3 className="text-title-md text-accent-ink leading-tight font-medium">{entry.title}</h3>
        <p className="text-meta-sm line-clamp-2 leading-snug text-neutral-500">
          {entry.description}
        </p>
      </div>
    </Link>
  );
}

function DarkActionRow({ entry }: { entry: WorkspaceEntry }) {
  const Icon = entry.icon;
  return (
    <Link
      to={entry.to as never}
      className={cn(
        'group flex items-center gap-3 rounded-xl border border-white/10 bg-white/5 px-4 py-3.5 backdrop-blur-sm',
        'transition-all duration-200 ease-out',
        'hover:-translate-y-0.5 hover:border-white/25 hover:bg-white/10',
        'focus-visible:ring-2 focus-visible:ring-white/50 focus-visible:outline-none',
        'motion-reduce:transform-none motion-reduce:hover:translate-y-0',
      )}
    >
      <div className="text-accent-ink flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-(--color-mint-paper) transition-colors group-hover:bg-white">
        <Icon className="h-4.5 w-4.5" strokeWidth={1.5} aria-hidden="true" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-body-sm truncate leading-tight font-semibold text-white">
          {entry.title}
        </div>
        <div className="text-meta-sm mt-0.5 truncate leading-tight text-white/55">
          {entry.description}
        </div>
      </div>
      <ArrowRight
        className={cn(
          'h-4 w-4 shrink-0 text-white/40 transition-all duration-200 ease-out',
          'group-hover:translate-x-0.5 group-hover:text-white',
        )}
        strokeWidth={1.75}
        aria-hidden="true"
      />
    </Link>
  );
}
